(() => {
  const ROOT_SELECTOR = '[data-product-variant-buttons]';
  const DATA_SELECTOR = '[data-product-variant-buttons-data]';
  const SELECT_SELECTOR = '[data-product-variant-select]';
  const GENERATED_SELECTOR = '[data-generated-product-variant-buttons]';
  const NATIVE_CLASS = 'q-native-variant-select--buttons-enhanced';
  const SELECT_HANDLER_KEY = '__qProductVariantButtonsChangeHandler';
  const MODULE_KEY = '__qProductVariantButtonsModule';
  const CHOICE_SELECTOR = '.q-product-variant-buttons__choice';
  const GROUP_SELECTOR = '.q-product-variant-buttons__group';
  const CONSENT_CLEANUP_KEY = '__qDigitalConsentCleanup';

  const existingModule = window[MODULE_KEY];
  if (existingModule && typeof existingModule.initialize === 'function') {
    existingModule.initialize(document);
    return;
  }

  const findRoot = (scope) => {
    if (scope?.matches?.(ROOT_SELECTOR)) return scope;
    return scope?.querySelector?.(ROOT_SELECTOR) || null;
  };

  const removeEnhancement = () => {
    document.querySelectorAll(GENERATED_SELECTOR).forEach((picker) => picker.remove());

    document.querySelectorAll(SELECT_SELECTOR).forEach((select) => {
      const oldHandler = select[SELECT_HANDLER_KEY];
      if (oldHandler) {
        select.closest('[data-product-form]')?.[CONSENT_CLEANUP_KEY]?.();
        select.removeEventListener('change', oldHandler);
        delete select[SELECT_HANDLER_KEY];
      }

      select.closest('.q-field')?.classList.remove(NATIVE_CLASS);
    });
  };

  const normalizeDigitalValue = (value) => value.trim().toLowerCase().replace(/[\s_-]+/g, '');

  const readDigitalConsent = (raw) => {
    if (!raw || raw.enabled !== true) return null;
    if (typeof raw.optionValues !== 'string'
      || typeof raw.text !== 'string' || !raw.text.trim()
      || typeof raw.propertyName !== 'string' || !raw.propertyName.trim()
      || /^_|[\[\]]/.test(raw.propertyName.trim())
      || typeof raw.propertyValue !== 'string' || !raw.propertyValue.trim()) return null;

    const optionValues = new Set(raw.optionValues.split(',').map(normalizeDigitalValue).filter(Boolean));
    return optionValues.size ? { ...raw, propertyName: raw.propertyName.trim(), optionValues } : null;
  };

  const readConfig = (root, core) => {
    const data = root.querySelector(DATA_SELECTOR);
    if (!data) return null;

    try {
      const raw = JSON.parse(data.textContent);
      if (!Array.isArray(raw?.options) || raw.options.length < 1 || raw.options.length > 3) {
        return null;
      }

      const config = core.normalizeConfig(raw);
      return config ? { ...config, digitalConsent: readDigitalConsent(raw.digitalConsent) } : null;
    } catch (_error) {
      return null;
    }
  };

  const matchingSelect = (config, scope = document) => {
    const candidates = [];
    if (scope?.matches?.(SELECT_SELECTOR)) candidates.push(scope);
    scope?.querySelectorAll?.(SELECT_SELECTOR).forEach((select) => candidates.push(select));

    return candidates.find((select) => {
      const form = select.closest('[data-product-form]');
      const layout = select.closest('.q-product-layout');
      if (!form || !layout || !layout.contains(form)) return false;

      const optionIds = new Set(Array.from(select.options, (option) => String(option.value)));
      return config.variants.every((variant) => optionIds.has(variant.id));
    }) || null;
  };

  const focusedChoiceDescriptor = (picker) => {
    const activeChoice = document.activeElement?.closest?.(CHOICE_SELECTOR);
    if (!activeChoice || !picker.contains(activeChoice)) return null;

    const group = activeChoice.closest(GROUP_SELECTOR);
    const groupIndex = Array.from(picker.children).indexOf(group);
    const value = activeChoice.getAttribute('aria-label');
    return groupIndex >= 0 && value !== null ? { groupIndex, value } : null;
  };

  const restoreChoiceFocus = (picker, descriptor) => {
    if (!descriptor) return;

    const group = picker.children[descriptor.groupIndex];
    const choice = Array.from(group?.querySelectorAll?.(CHOICE_SELECTOR) || [])
      .find((candidate) => candidate.getAttribute('aria-label') === descriptor.value);
    choice?.focus({ preventScroll: true });
  };

  const createChoice = ({
    choiceState,
    imageNodes,
    index,
    mode,
    select,
    value,
  }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `q-product-variant-buttons__choice q-product-variant-buttons__choice--${mode}`;
    button.setAttribute('aria-label', value);
    button.setAttribute('aria-pressed', String(choiceState.selected));
    button.setAttribute('aria-disabled', String(!choiceState.available));
    button.disabled = !choiceState.available;

    if (mode === 'image') {
      if (choiceState.imageUrl) {
        const imageKey = `${index}:${value}`;
        let image = imageNodes.get(imageKey);

        if (!image) {
          image = document.createElement('img');
          image.className = 'q-product-variant-buttons__image';
          image.alt = '';
          image.loading = 'lazy';
          image.width = 100;
          image.height = 100;
          imageNodes.set(imageKey, image);
        }

        if (image.getAttribute('src') !== choiceState.imageUrl) {
          image.src = choiceState.imageUrl;
        }
        button.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'q-product-variant-buttons__fallback';
        fallback.textContent = value;
        button.append(fallback);
      }
    } else {
      button.textContent = value;
    }

    button.addEventListener('click', () => {
      if (!choiceState.available || !choiceState.selectionVariant) return;

      select.value = choiceState.selectionVariant.id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return button;
  };

  const mount = ({ config, core, select }) => {
    const nativeField = select?.closest('.q-field');
    if (!select || !nativeField?.parentElement) return;

    const picker = document.createElement('div');
    picker.className = 'q-product-variant-buttons';
    picker.setAttribute('data-generated-product-variant-buttons', '');
    const imageNodes = new Map();
    const form = select.closest('[data-product-form]');
    const submit = form?.querySelector('[data-product-submit]');
    const isDigital = (variant) => Boolean(variant?.options.some((value) => (
      config.digitalConsent?.optionValues.has(normalizeDigitalValue(value))
    )));
    const consentController = config.digitalConsent && submit && config.variants.some(isDigital)
      ? window.QProductDigitalConsent?.createController({
        form, select, submit, variants: config.variants, isDigital, digitalConsent: config.digitalConsent,
      })
      : null;

    const render = () => {
      const focusDescriptor = focusedChoiceDescriptor(picker);
      const selectedVariant = config.variants.find((variant) => (
        variant.id === String(select.value)
      ));
      if (!selectedVariant) return false;

      const selections = selectedVariant.options;
      const groups = config.options.map((option, index) => {
        const mode = core.groupMode(config, index, selections);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'q-product-variant-buttons__group';

        const legend = document.createElement('legend');
        legend.className = 'q-product-variant-buttons__legend';
        legend.textContent = option.name;

        const choices = document.createElement('div');
        choices.className = 'q-product-variant-buttons__choices';
        const buttons = option.values.map((value) => createChoice({
          choiceState: core.choiceState(config, index, value, selections),
          imageNodes,
          index,
          mode,
          select,
          value,
        }));

        choices.replaceChildren(...buttons);
        fieldset.replaceChildren(legend, choices);
        return fieldset;
      });

      picker.replaceChildren(...groups);
      restoreChoiceFocus(picker, focusDescriptor);
      return true;
    };

    const handleSelectChange = () => {
      if (!render()) {
        removeEnhancement();
        return;
      }
      consentController?.synchronize();
      consentController?.queueSynchronization();
    };

    select[SELECT_HANDLER_KEY] = handleSelectChange;
    select.addEventListener('change', handleSelectChange);
    nativeField.before(picker);

    if (!render()) {
      removeEnhancement();
      return;
    }

    nativeField.classList.add(NATIVE_CLASS);
    consentController?.synchronize();
  };

  const coreAndConfig = (root) => {
    const core = window.QProductVariantButtonsCore;
    if (!core
      || typeof core.normalizeConfig !== 'function'
      || typeof core.groupMode !== 'function'
      || typeof core.choiceState !== 'function') {
      return null;
    }

    const config = readConfig(root, core);
    return config && config.options.length >= 1 && config.options.length <= 3
      ? { config, core }
      : null;
  };

  const initialize = (scope) => {
    const root = findRoot(scope);
    if (!root) return;

    removeEnhancement();
    const prepared = coreAndConfig(root);
    if (!prepared) return;

    const select = matchingSelect(prepared.config, document);
    if (select) mount({ ...prepared, select });
  };

  const initializeFromProductScope = (scope) => {
    const root = findRoot(document);
    if (!root) return;

    const prepared = coreAndConfig(root);
    if (!prepared) return;

    const select = matchingSelect(prepared.config, scope);
    if (!select) return;

    removeEnhancement();
    mount({ ...prepared, select });
  };

  const handleSectionLoad = (event) => {
    const scope = event.target;
    const root = findRoot(scope);
    if (root) {
      initialize(root);
      return;
    }

    const containsSelect = scope?.matches?.(SELECT_SELECTOR)
      || scope?.querySelector?.(SELECT_SELECTOR);
    if (containsSelect) initializeFromProductScope(scope);
  };

  const handleSectionUnload = (event) => {
    const scope = event.target;
    const containsProductUi = findRoot(scope)
      || scope?.matches?.(GENERATED_SELECTOR)
      || scope?.querySelector?.(GENERATED_SELECTOR);
    if (containsProductUi) removeEnhancement();
  };

  window[MODULE_KEY] = {
    initialize,
    removeEnhancement,
  };

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  initialize(document);
})();
